import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FiSmartphone,
    FiHome,
    FiBook,
    FiZap,
    FiShoppingCart,
    FiArrowRight,
    FiArrowUp,
} from 'react-icons/fi';
import { FaTshirt, FaPuzzlePiece, FaHamburger, FaRocket } from 'react-icons/fa';
import { GiLipstick, GiDiamondRing, GiRunningShoe, GiBackpack } from 'react-icons/gi';
import AxiosUrl from '../AxiosUrl';
import styles from './LandingHome.module.css';
import rusubdHome from '../../img/rusubd-home.png';
import rusubdShop from '../../img/rusubd-shop.png';
import rusubdBrand from '../../img/rusubd-brand.png';
import rusubdCombo from '../../img/rusubd-combo.png';
import rusubdBlog from '../../img/rusubd-blog.png';
import rusubdContact from '../../img/rusubd-contact.png';
import orderListImg from '../../img/orderlist.png';
import orderDetailImg from '../../img/orderdetail.png';
import pagespeedImg from '../../img/pagespeed.png';

const storefrontPages = [
    { src: rusubdHome,    label: 'হোম',     path: '/' },
    { src: rusubdShop,    label: 'শপ',      path: '/shop' },
    { src: rusubdBrand,   label: 'ব্র্যান্ড',  path: '/brand' },
    { src: rusubdCombo,   label: 'কম্বো',    path: '/category/combo-offer' },
    { src: rusubdBlog,    label: 'ব্লগ',    path: '/blog' },
    { src: rusubdContact, label: 'কন্টাক্ট', path: '/contact' },
];

const SLIDE_DURATION = 4500;

function StorefrontSlider() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return undefined;
        const t = setTimeout(() => {
            setActive((a) => (a + 1) % storefrontPages.length);
        }, SLIDE_DURATION);
        return () => clearTimeout(t);
    }, [active, paused]);

    const current = storefrontPages[active];

    return (
        <div
            className={styles.slider}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Browser chrome */}
            <div className={styles.sliderChrome}>
                <span></span><span></span><span></span>
                <div className={styles.sliderUrl}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.sliderLock}>
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    <span className={styles.sliderUrlHost}>rusubd.com</span>
                    <span className={styles.sliderUrlPath}>{current.path}</span>
                </div>
                <div className={styles.sliderChromeBadge}>LIVE</div>
            </div>

            {/* Slide stage with crossfade + slow zoom */}
            <div className={styles.sliderStage}>
                {storefrontPages.map((p, i) => (
                    <img
                        key={p.label}
                        src={p.src}
                        alt={p.label}
                        className={`${styles.slide} ${i === active ? styles.slideActive : ''}`}
                        loading={i === 0 ? 'eager' : 'lazy'}
                    />
                ))}
            </div>

            {/* Tab strip with progress bar on the active tab */}
            <div className={styles.sliderTabs}>
                {storefrontPages.map((p, i) => (
                    <button
                        key={p.label}
                        type="button"
                        className={`${styles.sliderTab} ${i === active ? styles.sliderTabActive : ''}`}
                        onClick={() => setActive(i)}
                        aria-label={p.label}
                    >
                        <span>{p.label}</span>
                        {i === active && (
                            <span
                                key={`bar-${active}-${paused}`}
                                className={styles.sliderTabBar}
                                style={{ animationPlayState: paused ? 'paused' : 'running' }}
                            />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ─── Inline icon set (lucide-style) ──────────────────────────────── */
const Icon = {
    ArrowRight: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
    ),
    Check: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ),
    Zap: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
    ),
    Shield: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
        </svg>
    ),
    ShieldAlert: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    Mail: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 6 12 13 2 6" />
        </svg>
    ),
    Phone: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z" />
        </svg>
    ),
    Truck: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
    ),
    CreditCard: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
        </svg>
    ),
    BarChart: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
        </svg>
    ),
    Globe: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
        </svg>
    ),
    Layout: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
        </svg>
    ),
    Megaphone: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
        </svg>
    ),
    Menu: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
    ),
    Close: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    Sparkles: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 5.8L20 11l-6.1 2.2L12 19l-1.9-5.8L4 11l6.1-2.2z" />
        </svg>
    ),
    Bell: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
    ),
    Cart: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
        </svg>
    ),
    Box: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
    ),
    Tag: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
    ),
    Server: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
            <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
            <line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
    ),
    Users: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
    ),
    Star: () => (
        <svg viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
    ),
    Quote: () => (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17h3l2-4V7H2v6h3zm10 0h3l2-4V7h-6v6h3z" />
        </svg>
    ),
    Plus: () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
    ),
    Whatsapp: () => (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
    ),
};

const features = [
    { Icon: Icon.Layout, title: 'স্টোরফ্রন্ট বিল্ডার', desc: 'কালার, ব্যানার, থিম ও ল্যান্ডিং পেজ — সব এক ড্যাশবোর্ড থেকে কাস্টমাইজ করুন, কোনো কোডিং ছাড়াই।' },
    { Icon: Icon.CreditCard, title: 'বিকাশ ও ক্যাশ অন ডেলিভারি', desc: 'বিল্ট-ইন বিকাশ পেমেন্ট গেটওয়ে ও সম্পূর্ণ COD অর্ডার ট্র্যাকিং।' },
    { Icon: Icon.ShieldAlert, title: 'ফ্রড কাস্টমার ডিটেকশন', desc: 'অর্ডার শিপ করার আগেই পাঠাও ও স্টেডফাস্টে কাস্টমারের ক্যান্সেল হিস্টরি অটো চেক।' },
    { Icon: Icon.Mail, title: 'নিজের ডোমেইনে বিজনেস ইমেইল', desc: 'you@yourstore.com থেকে ইমেইল পাঠান-গ্রহণ করুন, ইনবক্স, অটো-রিপ্লাই ও ট্র্যাকিং সহ।' },
    { Icon: Icon.Megaphone, title: 'এসএমএস মার্কেটিং ও অর্ডার এলার্ট', desc: 'অটোমেটিক্যালি কাস্টমারের কাছে পৌঁছান — ক্যাম্পেইন SMS ও অর্ডার স্ট্যাটাস নোটিফিকেশন।' },
    { Icon: Icon.Globe, title: 'ফ্রি SSL ও কাস্টম ডোমেইন', desc: 'এক ক্লিকে নিজের ডোমেইন কানেক্ট করুন HTTPS সহ — সুপার ফাস্ট BDIX হোস্টিং।' },
];

const whyValues = [
    { num: '১', title: 'ঝামেলাহীন সেটআপ', desc: 'রেজিস্ট্রেশনের পর কয়েক মিনিটেই আপনার স্টোর লাইভ। কোনো ডেভেলপার লাগবে না।' },
    { num: '২', title: 'বাংলাদেশের জন্য তৈরি', desc: 'বিকাশ, পাঠাও, স্টেডফাস্ট, RedX — সব লোকাল টুলের সাথে রেডি ইন্টিগ্রেশন।' },
    { num: '৩', title: 'BDIX ফাস্ট হোস্টিং', desc: 'লোকাল BDIX সার্ভারে সুপার ফাস্ট লোডিং ও আনলিমিটেড ট্রাফিক।' },
    { num: '৪', title: '২৪/৭ সাপোর্ট', desc: 'বাংলায় ফোন, হোয়াটসঅ্যাপ ও ইমেইল সাপোর্ট — যেকোনো সময়।' },
];

const categories = [
    { Icon: FaTshirt, name: 'ফ্যাশন' },
    { Icon: FiSmartphone, name: 'গ্যাজেট' },
    { Icon: GiLipstick, name: 'কসমেটিক্স' },
    { Icon: FaHamburger, name: 'খাবার' },
    { Icon: FiHome, name: 'গৃহস্থালি' },
    { Icon: FiBook, name: 'বই ও স্টেশনারি' },
    { Icon: FaPuzzlePiece, name: 'খেলনা' },
    { Icon: GiDiamondRing, name: 'গহনা' },
    { Icon: GiRunningShoe, name: 'জুতা' },
    { Icon: GiBackpack, name: 'ব্যাগ' },
    { Icon: FiZap, name: 'ইলেকট্রনিক্স' },
    { Icon: FiShoppingCart, name: 'অন্যান্য' },
];

const marketingBenefits = [
    { Icon: Icon.Cart, title: 'সুপার ইজি চেকআউট', desc: 'সিম্পল ও দ্রুত চেকআউট সিস্টেম — কার্ট অ্যাবান্ডনমেন্ট কমিয়ে আনুন।' },
    { Icon: Icon.Zap, title: 'সুপার ফাস্ট ওয়েবসাইট', desc: 'BDIX লোকাল হোস্টিং, অপ্টিমাইজড কোড — প্রতিটা পেজ চোখের পলকে লোড।' },
    { Icon: Icon.Layout, title: 'আনলিমিটেড ল্যান্ডিং পেজ', desc: 'প্রতিটা প্রোডাক্ট, প্রতিটা ক্যাম্পেইনের জন্য আলাদা ল্যান্ডিং পেজ বানান।' },
    { Icon: Icon.Shield, title: 'সিকিউর কাস্টমার ডাটা', desc: 'ফ্রি SSL সার্টিফিকেট ও সিকিউর ব্যাকআপ — আপনার ডাটা সম্পূর্ণ নিরাপদ।' },
];

const tools = [
    { Icon: Icon.Box, title: 'প্রোডাক্ট ম্যানেজমেন্ট', desc: 'বাল্ক প্রোডাক্ট আপলোড, স্টক, ভ্যারিয়েন্ট, ছবি — সব এক জায়গায়।' },
    { Icon: Icon.Cart, title: 'অর্ডার ম্যানেজমেন্ট', desc: 'অর্ডার দেখুন, এডিট করুন, ফিল্টার ও সিএসভি এক্সপোর্ট।' },
    { Icon: Icon.Tag, title: 'অফার ও কুপন', desc: 'ডিসকাউন্ট কোড, ফ্ল্যাট অফার, পার্সেন্ট অফ — কয়েক ক্লিকেই।' },
    { Icon: Icon.Truck, title: 'কুরিয়ার ইন্টিগ্রেশন', desc: 'এক ক্লিকে পাঠাও ও স্টেডফাস্টে অর্ডার পুশ, লেবেল প্রিন্ট।' },
    { Icon: Icon.BarChart, title: 'রিপোর্ট ও অ্যানালিটিক্স', desc: 'রিয়েল-টাইম সেলস, প্রোফিট, কাস্টমার অ্যানালিটিক্স ও রিপোর্ট।' },
    { Icon: Icon.Server, title: 'অটো এক্সপান্ডেবল সার্ভার', desc: 'যত বেশি ভিজিটর, তত বেশি স্কেল — কোনো ডাউনটাইম ছাড়াই।' },
];

// ── Pricing ──────────────────────────────────────────────────────────
// The first cards are loaded LIVE from the billing API (/billing-packages) —
// the exact same source the in-app Billing page uses — so the landing prices
// can never drift from what customers actually pay at checkout. The final
// "কাস্টম" card is a static contact-sales tier (no fixed price).

// Build a Bangla feature list from a package's structured fields.
const buildPackageFeatures = (pkg) => {
    const f = pkg.features || {};
    const l = pkg.limits || {};
    const out = [];

    if (l.orders != null) {
        out.push({
            text: l.orders === -1 ? 'আনলিমিটেড অর্ডার' : `${bn(l.orders)} অর্ডার/মাস`,
            strong: true,
        });
    }
    if (f.subDomain) out.push({ text: 'ফ্রি সাব-ডোমেইন', strong: true });
    if (l.teamMembers) out.push({ text: `${bn(l.teamMembers)} জন এমপ্লয়ী` });
    if (f.customDomain) out.push({ text: 'কাস্টম ডোমেইন + ফ্রি SSL' });
    if (f.bkashIntegration) out.push({ text: 'বিকাশ API ইন্টিগ্রেশন' });
    if (f.courierIntegration) out.push({ text: 'সব কুরিয়ার ইন্টিগ্রেশন' });
    if (f.pixelSetup) out.push({ text: 'ফেসবুক পিক্সেল সেটআপ' });
    if (f.facebookConversionsAPI) out.push({ text: 'ফেসবুক কনভার্সন API' });
    if (f.googleAnalytics) out.push({ text: 'গুগল অ্যানালিটিক্স ও ট্যাগ ম্যানেজার' });
    if (f.advancedReports) out.push({ text: 'অ্যাডভান্সড রিপোর্ট ও অ্যানালিটিক্স' });
    if (f.whatsappIntegration || f.messengerIntegration) {
        out.push({ text: 'হোয়াটসঅ্যাপ ও মেসেঞ্জার বাটন' });
    }
    if (f.smsNotifications) out.push({ text: 'SMS নোটিফিকেশন' });
    if (f.phoneSupport) out.push({ text: 'ফোন সাপোর্ট', strong: true });
    else if (f.prioritySupport) out.push({ text: 'প্রায়োরিটি সাপোর্ট' });
    else if (f.emailSupport) out.push({ text: 'ইমেইল সাপোর্ট' });

    return out;
};

// Map a /billing-packages record to the shape the pricing cards render.
const mapPackageToTier = (pkg) => ({
    id: pkg._id || pkg.packageType,
    packageType: pkg.packageType,              // drives the trial signup link
    name: pkg.nameBangla || pkg.name,
    tagline: pkg.descriptionBangla || pkg.description || '',
    monthly: pkg.pricing?.monthly?.price ?? 0, // monthly charge (৳/মাস)
    yearly: pkg.pricing?.yearly?.price ?? 0,   // full annual charge (৳/বছর)
    popular: pkg.packageType === 'business',
    features: buildPackageFeatures(pkg),
});

// Universal lines shown on EVERY plan card (platform-wide rates / policy).
const COMMON_FEATURES = [
    { text: 'এক্সট্রা অর্ডার চার্জ ৳১/অর্ডার' },
    { text: 'অটো কল রেট ৳০.৭০/মিনিট' },
    { text: 'AI কাস্টমার সাপোর্ট ৳৭/মিনিট' },
    { text: 'হিডেন চার্জ নেই', strong: true },
];

// Fallback tiers — used only if the billing API is unreachable, so the
// pricing section never renders empty. Values mirror the live packages.
const DEFAULT_TIERS = [
    {
        id: 'startup',
        packageType: 'startup',
        name: 'স্টার্টআপ প্যাকেজ',
        tagline: 'নতুন উদ্যোক্তা ও ছোট দোকানের জন্য',
        monthly: 500,
        yearly: 5000,
        popular: false,
        features: [
            { text: '৫০০ অর্ডার/মাস', strong: true },
            { text: 'ফ্রি সাব-ডোমেইন', strong: true },
            { text: 'সব কুরিয়ার ইন্টিগ্রেশন' },
            { text: 'ফেসবুক পিক্সেল সেটআপ' },
            { text: 'গুগল অ্যানালিটিক্স' },
            { text: 'হোয়াটসঅ্যাপ ও মেসেঞ্জার বাটন' },
            { text: 'ইমেইল সাপোর্ট' },
        ],
    },
    {
        id: 'smallBusiness',
        packageType: 'smallBusiness',
        name: 'স্মল বিজনেস প্যাকেজ',
        tagline: 'ছোট ব্যবসা ও স্টার্টআপের জন্য',
        monthly: 1000,
        yearly: 10000,
        popular: false,
        features: [
            { text: '১,৫০০ অর্ডার/মাস', strong: true },
            { text: 'ফ্রি সাব-ডোমেইন', strong: true },
            { text: 'সব কুরিয়ার ইন্টিগ্রেশন' },
            { text: 'ফেসবুক পিক্সেল ও কনভার্সন API' },
            { text: 'গুগল অ্যানালিটিক্স ও ট্যাগ ম্যানেজার' },
            { text: 'অ্যাডভান্সড রিপোর্ট' },
            { text: 'হোয়াটসঅ্যাপ ও মেসেঞ্জার বাটন' },
            { text: 'ইমেইল সাপোর্ট' },
        ],
    },
    {
        id: 'business',
        packageType: 'business',
        name: 'বিজনেস প্যাকেজ',
        tagline: 'গ্রোয়িং ব্যবসার জন্য',
        monthly: 4000,
        yearly: 40000,
        popular: true,
        features: [
            { text: '৫,০০০ অর্ডার/মাস', strong: true },
            { text: 'ফ্রি সাব-ডোমেইন', strong: true },
            { text: 'বিকাশ API ইন্টিগ্রেশন' },
            { text: 'সব কুরিয়ার ইন্টিগ্রেশন' },
            { text: 'ফেসবুক পিক্সেল ও কনভার্সন API' },
            { text: 'গুগল অ্যানালিটিক্স ও ট্যাগ ম্যানেজার' },
            { text: 'অ্যাডভান্সড রিপোর্ট' },
            { text: 'হোয়াটসঅ্যাপ ও মেসেঞ্জার বাটন' },
            { text: 'ফোন সাপোর্ট', strong: true },
        ],
    },
];

// Convert Latin digits in a number to Bangla digits with thousand separators
const bn = (num) => {
    const map = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
    return num.toLocaleString('en-US').replace(/\d/g, d => map[d]);
};

const testimonials = [
    {
        text: 'ইকমটেক ব্যবহার করে আমার ফ্যাশন ব্যবসা সম্পূর্ণ অটোমেটেড হয়ে গেছে। ফ্রড কাস্টমার চেকার ফিচারটা সবচেয়ে কাজের — প্রতি মাসে অনেক টাকা সেভ হচ্ছে।',
        name: 'রহিমা আক্তার',
        role: 'ফ্যাশন স্টোর, ঢাকা',
        avatar: 'র',
        bg: 'linear-gradient(135deg, #f472b6, #ec4899)',
    },
    {
        text: 'BDIX হোস্টিং আর সুপার ফাস্ট চেকআউটের কারণে আমার কনভার্শন রেট অনেক বেড়েছে। বিকাশ আর ক্যাশ অন ডেলিভারি দুটোই ঝামেলা ছাড়াই কাজ করে।',
        name: 'আশরাফুল ইসলাম',
        role: 'গ্যাজেট শপ, চট্টগ্রাম',
        avatar: 'আ',
        bg: 'linear-gradient(135deg, #34d399, #10b981)',
    },
    {
        text: 'একটাই ড্যাশবোর্ডে অর্ডার, কাস্টমার, কুরিয়ার, এসএমএস — সবকিছু। ছোট টিম নিয়েই অনেক বড় বিজনেস চালাতে পারছি।',
        name: 'সাদিয়া হাসান',
        role: 'কসমেটিক্স ব্র্যান্ড, সিলেট',
        avatar: 'স',
        bg: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
    },
];

const WHATSAPP_NUMBER = '8801711026578';
const WHATSAPP_MSG = 'হ্যালো ইকমটেক, আমি আপনাদের প্ল্যাটফর্ম সম্পর্কে জানতে চাই।';

const howSteps = [
    { num: '১', title: 'সাইন আপ করুন', desc: 'মোবাইল নম্বর দিয়ে ৩০ সেকেন্ডে অ্যাকাউন্ট তৈরি করুন, তারপর প্ল্যান বেছে নিন।' },
    { num: '২', title: 'প্রোডাক্ট অ্যাড করুন', desc: 'বাল্কে বা একটা একটা করে প্রোডাক্ট আপলোড করুন, ক্যাটাগরি ও দাম সেট করুন।' },
    { num: '৩', title: 'ডোমেইন কানেক্ট করুন', desc: 'নিজের কাস্টম ডোমেইন এক ক্লিকে কানেক্ট করুন — ফ্রি SSL অটো ইনস্টল হবে।' },
    { num: '৪', title: 'বিক্রি শুরু করুন', desc: 'বিকাশ ও ক্যাশ অন ডেলিভারি অন করুন, কুরিয়ার সেট করুন, আজই অর্ডার নিন।' },
];

const faqs = [
    {
        q: 'ইকমটেক ব্যবহার শুরু করতে কত টাকা লাগবে?',
        a: 'শুরু করতে একটি প্ল্যান কিনতে হবে — সর্বনিম্ন ৳৫০০/মাস থেকে। নতুন সাইন আপে ৪৮ ঘণ্টার বিশেষ লঞ্চ অফার: স্টার্টআপ প্ল্যান পুরো ১ বছর মাত্র ৳১,০০০।',
    },
    {
        q: 'নিজের ডোমেইন কীভাবে কানেক্ট করব?',
        a: 'ড্যাশবোর্ডে গিয়ে "Custom Domain" সেকশন থেকে আপনার ডোমেইন লিখে সাবমিট করুন। আমরা DNS সেটআপ গাইড দেব এবং SSL সার্টিফিকেট অটোমেটিক্যালি ইনস্টল হয়ে যাবে। সাধারণত ১০-৩০ মিনিটে লাইভ হয়ে যায়।',
    },
    {
        q: 'বিকাশ পেমেন্ট কীভাবে কাজ করে?',
        a: 'ইকমটেকে বিকাশ পেমেন্ট গেটওয়ে বিল্ট-ইন আছে। আপনার বিকাশ মার্চেন্ট অ্যাকাউন্ট কানেক্ট করলেই কাস্টমাররা সরাসরি পে করতে পারবে। প্রতি ট্রানজেকশনে বিকাশের নিজস্ব চার্জ প্রযোজ্য — ইকমটেক কোনো অতিরিক্ত ফি নেয় না।',
    },
    {
        q: 'ফ্রড কাস্টমার ডিটেকশন কীভাবে কাজ করে?',
        a: 'প্রতিটা COD অর্ডারের জন্য আমরা অটোমেটিক্যালি কাস্টমারের ফোন নম্বর পাঠাও ও স্টেডফাস্টে চেক করি — তাদের মোট ডেলিভারি, সফল ডেলিভারি, ক্যান্সেল রেট ও পুরোনো ফ্রড ফ্ল্যাগ দেখাই। আপনি অর্ডার পুশ করার আগেই বুঝতে পারবেন কাস্টমার নিরাপদ কিনা।',
    },
    {
        q: 'টেকনিক্যাল সাপোর্ট কোন ভাষায় পাবো?',
        a: 'সম্পূর্ণ বাংলায়। আমাদের সাপোর্ট টিম WhatsApp, ফোন ও ইমেইল — তিন চ্যানেলেই বাংলায় সাপোর্ট দেয়। ফোন ও প্রায়োরিটি সাপোর্ট বিজনেস প্ল্যানে আছে।',
    },
    {
        q: 'প্ল্যান আপগ্রেড বা ক্যান্সেল করতে পারব?',
        a: 'হ্যাঁ, যেকোনো সময়। প্ল্যান আপগ্রেড করলে নতুন প্ল্যানের ফিচার সাথে সাথে অ্যাক্টিভ হয়ে যাবে। ক্যান্সেল করলে বর্তমান বিলিং পিরিয়ড শেষ হওয়া পর্যন্ত আপনার অ্যাকসেস থাকবে।',
    },
    {
        q: 'আমার পুরোনো ডাটা মাইগ্রেট করা যাবে?',
        a: 'হ্যাঁ। CSV ফাইলের মাধ্যমে প্রোডাক্ট, ক্যাটাগরি, কাস্টমার ও অর্ডার ইম্পোর্ট করা যায়। যদি অন্য প্ল্যাটফর্ম থেকে আসেন, আমাদের সাপোর্ট টিম মাইগ্রেশনে সাহায্য করবে।',
    },
    {
        q: 'কতগুলো প্রোডাক্ট অ্যাড করতে পারব?',
        a: 'সব প্ল্যানে আনলিমিটেড প্রোডাক্ট। অর্ডার সংখ্যা ও এমপ্লয়ী সংখ্যা প্ল্যান অনুযায়ী লিমিটেড।',
    },
    {
        q: 'ওয়েবসাইট কি মোবাইলে ভালো লোড হবে?',
        a: 'অবশ্যই। আমাদের সব থিম মোবাইল-ফার্স্ট, BDIX লোকাল হোস্টিংয়ে চলে, এবং Google PageSpeed-এ ৯৫+ স্কোর পায়। মানে আপনার কাস্টমাররা পাবে সুপার ফাস্ট লোডিং।',
    },
    {
        q: 'সাবস্ক্রিপশন বন্ধ করলে আমার ডাটার কী হবে?',
        a: 'আপনার ডাটা ৬০ দিন পর্যন্ত সংরক্ষিত থাকবে। এই সময়ের মধ্যে আপনি ডাটা এক্সপোর্ট করতে পারবেন বা প্ল্যান রিনিউ করে সব ফিরিয়ে আনতে পারবেন।',
    },
];

function LandingHome() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [billing, setBilling] = useState('yearly'); // 'monthly' | 'yearly'
    const [openFaq, setOpenFaq] = useState(0);
    // Pricing cards: live packages from /billing-packages + static custom tier.
    const [tiers, setTiers] = useState(DEFAULT_TIERS);

    useEffect(() => {
        let active = true;
        AxiosUrl.get('/billing-packages')
            .then((res) => {
                const list = res?.data?.data;
                if (active && Array.isArray(list) && list.length) {
                    setTiers(list.map(mapPackageToTier));
                }
            })
            .catch((err) => {
                console.error('Failed to load pricing packages:', err);
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        document.title = 'ইকমটেক — বাংলাদেশের সেরা ইকমার্স প্ল্যাটফর্ম';
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const closeMenu = () => setMenuOpen(false);

    const scrollToTop = (e) => {
        e.preventDefault();
        setMenuOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className={`${styles.page} ${menuOpen ? styles.mobileDrawerOpen : ''}`} lang="bn">

            {/* ─── NAV ──────────────────────────────────────────── */}
            <nav className={`${styles.nav} ${scrolled ? styles.navScrolled : ''}`}>
                <div className={styles.navInner}>
                    <Link to="/" className={styles.brand} onClick={scrollToTop}>
                        <span className={styles.brandLogo}>
                            <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="EcomTech" />
                        </span>
                        ইকমটেক
                    </Link>

                    <ul className={styles.navLinks}>
                        <li><a href="#how">কীভাবে</a></li>
                        <li><a href="#features">ফিচার</a></li>
                        <li><a href="#pricing">প্রাইসিং</a></li>
                        <li><a href="#fraud">ফ্রড চেক</a></li>
                        <li><a href="#faq">প্রশ্ন</a></li>
                    </ul>

                    <div className={styles.navCtas}>
                        <Link to="/?tab=login" className={styles.btnGhost}>লগইন</Link>
                        <Link to="/?tab=signup" className={styles.btnPrimary}>
                            এখনই শুরু করুন <Icon.ArrowRight />
                        </Link>
                        <button
                            type="button"
                            className={styles.menuBtn}
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="মেনু"
                            aria-expanded={menuOpen}
                        >
                            {menuOpen ? <Icon.Close /> : <Icon.Menu />}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer */}
                <div className={styles.mobileDrawer}>
                    <ul>
                        <li><a href="#how" onClick={closeMenu}>কীভাবে</a></li>
                        <li><a href="#features" onClick={closeMenu}>ফিচার</a></li>
                        <li><a href="#pricing" onClick={closeMenu}>প্রাইসিং</a></li>
                        <li><a href="#fraud" onClick={closeMenu}>ফ্রড চেক</a></li>
                        <li><a href="#faq" onClick={closeMenu}>প্রশ্ন</a></li>
                    </ul>
                    <div className={styles.mobileDrawerCtas}>
                        <Link to="/?tab=login" className={styles.btnGhost} onClick={closeMenu}>লগইন</Link>
                        <Link to="/?tab=signup" className={styles.btnPrimary} onClick={closeMenu}>
                            এখনই শুরু করুন <Icon.ArrowRight />
                        </Link>
                    </div>
                </div>
            </nav>

            {/* ─── HERO ─────────────────────────────────────────── */}
            <header className={styles.hero}>
                <div className={styles.container}>
                    <div className={styles.heroInner}>

                        <div className={styles.heroLeft}>
                            <span className={styles.heroBadge}>
                                <span></span>
                                বাংলাদেশের সেলারদের জন্য তৈরি
                            </span>

                            <h1 className={styles.heroHeading}>
                                সুপার ফাস্ট ওয়েবসাইটে{' '}
                                <span className={styles.grad}>ইকমার্স বিজনেস হবে অটোমেটেড</span>
                            </h1>

                            <p className={styles.heroSub}>
                                ইকমটেক হলো বাংলাদেশের জন্য তৈরি অল-ইন-ওয়ান কমার্স প্ল্যাটফর্ম —
                                প্রোডাক্ট, অর্ডার, বিকাশ, কুরিয়ার ইন্টিগ্রেশন, ফ্রড চেক, এসএমএস, ইমেইল ও
                                ফাস্ট BDIX হোস্টেড স্টোর। একটাই ড্যাশবোর্ড, কোনো কোডিং ছাড়াই।
                            </p>

                            <div className={styles.heroCtas}>
                                <Link to="/?tab=signup" className={`${styles.btnPrimary} ${styles.btnLarge}`}>
                                    স্টোর শুরু করুন <Icon.ArrowRight />
                                </Link>
                                <Link to="/?tab=login" className={`${styles.btnGhost} ${styles.btnLarge}`}>
                                    ড্যাশবোর্ডে লগইন
                                </Link>
                            </div>

                            <div className={styles.heroMeta}>
                                <span className={styles.heroTrialBadge}>
                                    <Icon.Sparkles />
                                    <strong>লঞ্চ অফার: ১ বছর ৳১,০০০</strong>
                                </span>
                                <span className={styles.check}>
                                    <Icon.Check /> বিকাশে সহজ পেমেন্ট
                                </span>
                                <span className={styles.check}>
                                    <Icon.Check /> কয়েক মিনিটেই সেটআপ
                                </span>
                            </div>
                        </div>

                        <div className={styles.heroVisual}>
                            <div className={styles.heroCard}>
                                <div className={styles.heroCardTop}>
                                    <span></span><span></span><span></span>
                                </div>

                                <div className={styles.heroStatGrid}>
                                    <div className={styles.heroStatCard}>
                                        <p className={styles.heroStatLabel}>আজকের সেল</p>
                                        <p className={styles.heroStatValue}>৳ ৪৮,২৫০</p>
                                        <p className={styles.heroStatDelta}><FiArrowUp style={{verticalAlign:'middle'}} /> গতকালের চেয়ে বেশি</p>
                                    </div>
                                    <div className={styles.heroStatCard}>
                                        <p className={styles.heroStatLabel}>নতুন কাস্টমার</p>
                                        <p className={styles.heroStatValue}>৩২</p>
                                        <p className={styles.heroStatDelta}><FiArrowUp style={{verticalAlign:'middle'}} /> আজ ৮ জন নতুন</p>
                                    </div>
                                    <div className={styles.heroStatCard}>
                                        <p className={styles.heroStatLabel}>পেন্ডিং COD</p>
                                        <p className={styles.heroStatValue}>১৪</p>
                                        <p className={styles.heroStatDelta}>২টি রিস্ক ফ্ল্যাগ</p>
                                    </div>
                                    <div className={styles.heroStatCard}>
                                        <p className={styles.heroStatLabel}>কনভার্শন</p>
                                        <p className={styles.heroStatValue}>৩.৮%</p>
                                        <p className={styles.heroStatDelta}><FiArrowUp style={{verticalAlign:'middle'}} /> এই সপ্তাহে</p>
                                    </div>
                                </div>

                                <div className={styles.heroChart}>
                                    <div className={styles.heroChartTop}>
                                        <span>৭ দিনের আয়</span>
                                        <strong>৳ ২,৮৪,৫০০</strong>
                                    </div>
                                    <svg viewBox="0 0 300 70" className={styles.heroChartSvg} preserveAspectRatio="none">
                                        <defs>
                                            <linearGradient id="grad1" x1="0" x2="0" y1="0" y2="1">
                                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.55" />
                                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        <path d="M0,55 L40,42 L80,48 L120,28 L160,35 L200,18 L240,22 L280,8 L300,12 L300,70 L0,70 Z" fill="url(#grad1)" />
                                        <path d="M0,55 L40,42 L80,48 L120,28 L160,35 L200,18 L240,22 L280,8 L300,12" stroke="#60a5fa" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </div>

                            <div className={`${styles.floatBadge} ${styles.floatBadgeA}`}>
                                <span className={styles.icon}><Icon.Bell /></span>
                                ৩টি নতুন অর্ডার
                            </div>
                            <div className={`${styles.floatBadge} ${styles.floatBadgeB}`}>
                                <span className={styles.icon}><Icon.ShieldAlert /></span>
                                ফ্রড ব্লকড
                            </div>
                        </div>

                    </div>
                </div>
            </header>

            {/* ─── TRUST BAR ────────────────────────────────────── */}
            <section className={styles.trustBar}>
                <div className={styles.container}>
                    <p className={styles.trustBarLabel}>যেসব টুলের সাথে আমরা ইন্টিগ্রেটেড</p>
                    <div className={styles.trustBarItems}>
                        <div>বিকাশ</div>
                        <div>পাঠাও কুরিয়ার</div>
                        <div>স্টেডফাস্ট</div>
                        <div>RedX</div>
                        <div>SSLCommerz</div>
                        <div>ক্যাশ অন ডেলিভারি</div>
                    </div>
                </div>
            </section>

            {/* ─── WHY ECOMTECH (4 value props) ─────────────────── */}
            <section id="why" className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>কেন ইকমটেক?</p>
                        <h2 className={styles.sectionTitle}>
                            কেন বাংলাদেশের সেলাররা <span className={styles.grad}>ইকমটেক বেছে নিচ্ছেন</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            ছোট থেকে বড় — সব ধরনের ব্যবসার জন্য একটাই সম্পূর্ণ সমাধান।
                        </p>
                    </div>

                    <div className={styles.whyGrid}>
                        {whyValues.map(v => (
                            <div key={v.title} className={styles.whyCard}>
                                <div className={styles.whyCardNum}>{v.num}</div>
                                <h3 className={styles.whyCardTitle}>{v.title}</h3>
                                <p className={styles.whyCardDesc}>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── WHAT IS ECOMTECH ─────────────────────────────── */}
            <section className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.whatWrap}>
                        <span className={styles.whatBadge}>ইকমটেক কী?</span>
                        <h2 className={styles.whatTitle}>
                            অনলাইন ব্যবসার জন্য অল-ইন-ওয়ান অটোমেশন সিস্টেম
                        </h2>
                        <p className={styles.whatDesc}>
                            স্টোর তৈরি থেকে অর্ডার ডেলিভারি পর্যন্ত — প্রতিটা ধাপ একটাই প্ল্যাটফর্মে।
                            আপনাকে আর আলাদা আলাদা টুল ব্যবহার করতে হবে না, একাধিক সাবস্ক্রিপশন কিনতে হবে না।
                            ইকমটেক দিয়ে আপনি সম্পূর্ণ অটোমেটেড একটা ইকমার্স বিজনেস চালাতে পারবেন।
                        </p>
                        <div className={styles.whatPoints}>
                            <span className={styles.whatPoint}><Icon.Check /> অটোমেটেড অর্ডার প্রসেসিং</span>
                            <span className={styles.whatPoint}><Icon.Check /> বিল্ট-ইন পেমেন্ট গেটওয়ে</span>
                            <span className={styles.whatPoint}><Icon.Check /> মাল্টি-চ্যানেল কুরিয়ার</span>
                            <span className={styles.whatPoint}><Icon.Check /> ফ্রড প্রোটেকশন</span>
                            <span className={styles.whatPoint}><Icon.Check /> ২৪/৭ সাপোর্ট</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── HOW IT WORKS (4 steps) ──────────────────────── */}
            <section id="how" className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>কীভাবে শুরু করবেন</p>
                        <h2 className={styles.sectionTitle}>
                            মাত্র ৪টি স্টেপে <span className={styles.grad}>লাইভ স্টোর</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            ডেভেলপার লাগবে না, ডিজাইনার লাগবে না — আপনি নিজেই সব করতে পারবেন।
                        </p>
                    </div>

                    <div className={styles.stepsGrid}>
                        {howSteps.map((s, i) => (
                            <div key={s.num} className={styles.stepCard}>
                                <div className={styles.stepConnector}>
                                    <div className={styles.stepNum}>{s.num}</div>
                                    {i < howSteps.length - 1 && <div className={styles.stepLine} />}
                                </div>
                                <h3 className={styles.stepTitle}>{s.title}</h3>
                                <p className={styles.stepDesc}>{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── FEATURES GRID ───────────────────────────────── */}
            <section id="features" className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>সব ফিচার এক জায়গায়</p>
                        <h2 className={styles.sectionTitle}>
                            একটাই ড্যাশবোর্ড। <span className={styles.grad}>সব কমার্স টুল।</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            ভিজিটর আপনার স্টোরে আসা থেকে শুরু করে কুরিয়ার তাদের প্যাকেজ ডেলিভারি দেওয়া পর্যন্ত —
                            প্রতিটা ধাপ বিল্ট-ইন।
                        </p>
                    </div>

                    <div className={styles.featureGrid}>
                        {features.map(({ Icon: I, title, desc }) => (
                            <article key={title} className={styles.featureCard}>
                                <div className={styles.featureCardIcon}><I /></div>
                                <h3 className={styles.featureCardTitle}>{title}</h3>
                                <p className={styles.featureCardDesc}>{desc}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── INDUSTRY CATEGORIES ─────────────────────────── */}
            <section className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>সব ব্যবসার জন্য</p>
                        <h2 className={styles.sectionTitle}>
                            যেকোনো ব্যবসার জন্য <span className={styles.grad}>রেডি টেমপ্লেট</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            আপনার ব্যবসা যেটাই হোক — ফ্যাশন, গ্যাজেট, কসমেটিক্স বা খাবার — ইকমটেকে রেডি টেমপ্লেট আছে।
                        </p>
                    </div>

                    <div className={styles.categoryGrid}>
                        {categories.map(({ Icon: I, name }) => (
                            <div key={name} className={styles.categoryItem}>
                                <span className={styles.categoryEmoji}><I /></span>
                                <p className={styles.categoryName}>{name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── SHOWCASE 1 + 2 ──────────────────────────────── */}
            <section id="showcase" className={styles.section}>
                <div className={styles.container}>

                    <div className={styles.showcase}>
                        <div className={styles.showcaseText}>
                            <p className={styles.showcaseEyebrow}>স্টোরফ্রন্ট বিল্ডার</p>
                            <h2 className={styles.showcaseTitle}>
                                ১০ মিনিটেই লঞ্চ করুন একটি সুন্দর স্টোর
                            </h2>
                            <p className={styles.showcaseDesc}>
                                একটা থিম বেছে নিন, প্রোডাক্ট অ্যাড করুন, ডোমেইন কানেক্ট করুন — আপনার স্টোর লাইভ।
                                কোনো ডিজাইনার লাগবে না, ডেভেলপার লাগবে না, হোস্টিং নিয়ে দুশ্চিন্তা নেই।
                            </p>
                            <ul className={styles.showcaseList}>
                                <li><Icon.Check /> মোবাইল-ফার্স্ট, BDIX হোস্টেড, SSL সহ</li>
                                <li><Icon.Check /> ড্র্যাগ-অ্যান্ড-ড্রপ ব্যানার ও ল্যান্ডিং পেজ বিল্ডার</li>
                                <li><Icon.Check /> এক ক্লিকেই কাস্টম ডোমেইন কানেক্ট</li>
                                <li><Icon.Check /> কালার, ফন্ট, থিম কাস্টমাইজ — কোডিং ছাড়াই</li>
                            </ul>
                            <div>
                                <Link to="/?tab=signup" className={`${styles.btnPrimary} ${styles.btnLarge}`}>
                                    বিল্ডার ব্যবহার করুন <Icon.ArrowRight />
                                </Link>
                            </div>
                        </div>

                        <div className={styles.showcaseImageWrap}>
                            <StorefrontSlider />
                        </div>
                    </div>

                    <div className={`${styles.showcase} ${styles.showcaseReverse}`}>
                        <div className={styles.showcaseText}>
                            <p className={styles.showcaseEyebrow}>অর্ডার ম্যানেজমেন্ট</p>
                            <h2 className={styles.showcaseTitle}>
                                প্রতিটা অর্ডার, প্রতিটা কাস্টমার — একই ছাদের নিচে
                            </h2>
                            <p className={styles.showcaseDesc}>
                                অর্ডার শুরু থেকে শেষ পর্যন্ত ট্র্যাক করুন। এক ক্লিকে পাঠাও বা স্টেডফাস্টে পুশ করুন,
                                বাল্কে ইনভয়েস ও স্টিকার প্রিন্ট করুন, কাস্টমারকে অটোমেটিক্যালি SMS পাঠান।
                            </p>
                            <ul className={styles.showcaseList}>
                                <li><Icon.Check /> পাঠাও ও স্টেডফাস্টে বাল্ক কুরিয়ার পুশ</li>
                                <li><Icon.Check /> এক ক্লিকে ইনভয়েস ও শিপিং স্টিকার প্রিন্ট</li>
                                <li><Icon.Check /> অর্ডার স্ট্যাটাস চেঞ্জে অটো SMS</li>
                                <li><Icon.Check /> ডেট ফিল্টার, সার্চ, সর্ট ও CSV এক্সপোর্ট</li>
                            </ul>
                            <div>
                                <Link to="/?tab=signup" className={`${styles.btnPrimary} ${styles.btnLarge}`}>
                                    অর্ডার ড্যাশবোর্ড দেখুন <Icon.ArrowRight />
                                </Link>
                            </div>
                        </div>

                        <div className={styles.showcaseImageWrap}>
                            <div className={styles.showcaseImage}>
                                <img src={orderListImg} alt="EcomTech order list" />
                            </div>
                            <div className={styles.showcaseOverlay}>
                                <img src={orderDetailImg} alt="EcomTech order detail" />
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ─── PAGESPEED PROOF ─────────────────────────────── */}
            <section id="speed" className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.speedWrap}>

                        <div className={styles.speedLeft}>
                            <span className={styles.speedBadge}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                </svg>
                                Google PageSpeed Insights
                            </span>
                            <h2 className={styles.speedTitle}>
                                আপনার স্টোর হবে <span className={styles.grad}>৯৫+ স্কোর</span>{' '}
                                পারফরম্যান্স
                            </h2>
                            <p className={styles.speedDesc}>
                                অপটিমাইজড কোড আর BDIX লোকাল হোস্টিং — এটাই দিচ্ছে রিয়েল-ওয়ার্ল্ড স্পিড।
                                দ্রুত লোডিং মানে বেশি কনভার্শন, কম বাউন্স, আর Google সার্চে ভালো র‍্যাঙ্কিং।
                            </p>

                            <div className={styles.speedStats}>
                                <div className={styles.speedStat}>
                                    <div className={`${styles.speedScore} ${styles.scoreGreen}`}>৯৫</div>
                                    <div className={styles.speedStatLabel}>Performance</div>
                                </div>
                                <div className={styles.speedStat}>
                                    <div className={`${styles.speedScore} ${styles.scoreGreen}`}>৯৪</div>
                                    <div className={styles.speedStatLabel}>Accessibility</div>
                                </div>
                                <div className={styles.speedStat}>
                                    <div className={`${styles.speedScore} ${styles.scoreGreen}`}>১০০</div>
                                    <div className={styles.speedStatLabel}>Best Practices</div>
                                </div>
                                <div className={styles.speedStat}>
                                    <div className={`${styles.speedScore} ${styles.scoreGreen}`}>৯২</div>
                                    <div className={styles.speedStatLabel}>SEO</div>
                                </div>
                            </div>

                            <ul className={styles.speedList}>
                                <li><Icon.Check /> <strong>১.২s</strong> First Contentful Paint</li>
                                <li><Icon.Check /> <strong>২.৮s</strong> Largest Contentful Paint</li>
                                <li><Icon.Check /> <strong>২০ms</strong> Total Blocking Time</li>
                                <li><Icon.Check /> <strong>০.০১১</strong> Cumulative Layout Shift</li>
                            </ul>
                        </div>

                        <div className={styles.speedRight}>
                            <div className={styles.speedCard}>
                                <img src={pagespeedImg} alt="Google PageSpeed Insights — 95+ score" />
                            </div>
                            <div className={styles.speedFloat}>
                                <div className={styles.speedFloatScore}>৯৫</div>
                                <div className={styles.speedFloatLabel}>
                                    <strong>Excellent</strong>
                                    <span>Real-world speed</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ─── MARKETING BENEFITS (4 cards) ─────────────────── */}
            <section className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>মার্কেটিং বেনিফিট</p>
                        <h2 className={styles.sectionTitle}>
                            কম খরচে <span className={styles.grad}>সর্বোচ্চ সেল</span> <FaRocket style={{verticalAlign:'middle'}} />
                        </h2>
                        <p className={styles.sectionSub}>
                            ফাস্ট ওয়েবসাইট, সিকিউর চেকআউট আর আনলিমিটেড ল্যান্ডিং পেজ — সবকিছু আপনার সেল বাড়াতে।
                        </p>
                    </div>

                    <div className={styles.marketingGrid}>
                        {marketingBenefits.map(({ Icon: I, title, desc }) => (
                            <article key={title} className={styles.marketingCard}>
                                <div className={styles.marketingIcon}><I /></div>
                                <h3 className={styles.marketingTitle}>{title}</h3>
                                <p className={styles.marketingDesc}>{desc}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── ORDER TOOLS (6 cards) ────────────────────────── */}
            <section className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>বিজনেস কন্ট্রোল</p>
                        <h2 className={styles.sectionTitle}>
                            পুরো বিজনেস কন্ট্রোল হবে <span className={styles.grad}>ড্যাশবোর্ড থেকেই</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            প্রোডাক্ট থেকে রিপোর্ট পর্যন্ত — সব কিছুর জন্য আলাদা টুল লাগবে না।
                        </p>
                    </div>

                    <div className={styles.toolsGrid}>
                        {tools.map(({ Icon: I, title, desc }) => (
                            <div key={title} className={styles.toolCard}>
                                <div className={styles.toolIcon}><I /></div>
                                <div>
                                    <h3 className={styles.toolTitle}>{title}</h3>
                                    <p className={styles.toolDesc}>{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── PRICING ─────────────────────────────────────── */}
            <section id="pricing" className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>প্রাইসিং</p>
                        <h2 className={styles.sectionTitle}>
                            ছোট থেকে বড় — <span className={styles.grad}>সব ব্যবসার জন্য একটি প্ল্যান</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            অর্ডার ও এমপ্লয়ী সংখ্যার ভিত্তিতে বেছে নিন আপনার প্ল্যান।
                            ইয়ারলি বিলিংয়ে ১৭% পর্যন্ত সাশ্রয় করুন।
                        </p>
                    </div>

                    {/* Monthly / Yearly toggle */}
                    <div className={styles.billingToggle}>
                        <button
                            type="button"
                            className={billing === 'monthly' ? styles.billingActive : ''}
                            onClick={() => setBilling('monthly')}
                        >
                            মাসিক
                        </button>
                        <button
                            type="button"
                            className={billing === 'yearly' ? styles.billingActive : ''}
                            onClick={() => setBilling('yearly')}
                        >
                            বার্ষিক
                            <span className={styles.billingSave}>১৭% সাশ্রয়</span>
                        </button>
                    </div>

                    {/* Tier cards */}
                    <div className={styles.pricingGrid}>
                        {tiers.map(tier => {
                            // DB stores the full annual price in `yearly`; show the
                            // per-month equivalent as the headline figure.
                            const perMonth = billing === 'yearly'
                                ? Math.round(tier.yearly / 12)
                                : tier.monthly;
                            return (
                                <div
                                    key={tier.id}
                                    className={`${styles.pricingCard} ${tier.popular ? styles.pricingPopular : ''}`}
                                >
                                    {tier.popular && (
                                        <div className={styles.popularBadge}>সবচেয়ে জনপ্রিয়</div>
                                    )}
                                    <div className={styles.pricingHeader}>
                                        <h3 className={styles.pricingName}>{tier.name}</h3>
                                        <p className={styles.pricingTagline}>{tier.tagline}</p>
                                    </div>

                                    <div className={styles.pricingPriceBlock}>
                                        <div className={styles.pricingPrice}>
                                            <span className={styles.pricingCurrency}>৳</span>
                                            <span className={styles.pricingAmount}>{bn(perMonth)}</span>
                                            <span className={styles.pricingPer}>/মাস</span>
                                        </div>
                                        {billing === 'yearly' ? (
                                            <p className={styles.pricingSub}>
                                                ৳{bn(tier.yearly)} বার্ষিক বিল
                                                <span className={styles.pricingSlash}>৳{bn(tier.monthly)}/মাস</span>
                                            </p>
                                        ) : (
                                            <p className={styles.pricingSub}>মাসিক বিল</p>
                                        )}
                                    </div>

                                    <ul className={styles.pricingFeatures}>
                                        {tier.features.map((f, i) => (
                                            <li key={i}>
                                                <Icon.Check />
                                                {f.strong ? <strong>{f.text}</strong> : f.text}
                                            </li>
                                        ))}
                                        {COMMON_FEATURES.map((f, i) => (
                                            <li key={`common-${i}`}>
                                                <Icon.Check />
                                                {f.strong ? <strong>{f.text}</strong> : f.text}
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        to={`/?tab=signup&plan=${tier.packageType}`}
                                        className={`${styles.btnPrimary} ${styles.btnLarge} ${styles.pricingCta}`}
                                    >
                                        এই প্ল্যানে শুরু করুন <Icon.ArrowRight />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>

                    <p className={styles.pricingNote}>
                        নতুন সাইন আপে ৪৮ ঘণ্টার লঞ্চ অফার — স্টার্টআপ ১ বছর মাত্র ৳১,০০০। যেকোনো সময় বাতিল করুন।
                    </p>
                </div>
            </section>

            {/* ─── FRAUD HIGHLIGHT ─────────────────────────────── */}
            <section id="fraud" className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.fraudWrap}>

                        <div className={styles.fraudLeft}>
                            <p className={styles.sectionEyebrow}>ফ্রড কাস্টমার ডিটেকশন</p>
                            <h2 className={styles.fraudTitle}>
                                <span className={styles.red}>ফেক COD কাস্টমারের</span> কাছে পাঠানো বন্ধ করুন।{' '}
                                <span className={styles.green}>প্রতিটা টাকা সেভ করুন।</span>
                            </h2>
                            <p className={styles.fraudDesc}>
                                একটা Cash-on-Delivery অর্ডার কুরিয়ারে পুশ করার আগেই ইকমটেক অটোমেটিক্যালি কাস্টমারের
                                পাঠাও ও স্টেডফাস্ট হিস্টরি চেক করে দেয় — যাতে আপনি জানতে পারেন কাকে শিপ করা নিরাপদ।
                            </p>

                            <div className={styles.fraudFeatures}>
                                <div className={styles.fraudFeature}>
                                    <span className={styles.icon}><Icon.Truck /></span>
                                    <div className={styles.text}>
                                        প্রতিটা কাস্টমারের <strong>পাঠাও + স্টেডফাস্ট</strong> ডেলিভারি হিস্টরি চেক
                                    </div>
                                </div>
                                <div className={styles.fraudFeature}>
                                    <span className={styles.icon}><Icon.BarChart /></span>
                                    <div className={styles.text}>
                                        <strong>ক্যান্সেল রেট,</strong> মোট ডেলিভারি ও পুরোনো ফ্রড ফ্ল্যাগ দেখুন
                                    </div>
                                </div>
                                <div className={styles.fraudFeature}>
                                    <span className={styles.icon}><Icon.ShieldAlert /></span>
                                    <div className={styles.text}>
                                        অর্ডার লিস্টে অটো <strong>রিস্ক ব্যাজ</strong> — ঝুঁকি আগে থেকেই বুঝুন
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.fraudCard}>
                            <div className={styles.fraudCardTop}>
                                <p className={styles.fraudCardTitle}>কাস্টমার রিপুটেশন</p>
                                <span className={styles.fraudCardBadge}>হাই রিস্ক</span>
                            </div>

                            <div className={styles.fraudCardCustomer}>
                                <div className={styles.fraudAvatar}>র</div>
                                <div className={styles.fraudCustomerInfo}>
                                    <span className={styles.name}>০১৭XX-XXX-১২৩৪</span>
                                    <span className={styles.phone}>এইমাত্র চেক করা</span>
                                </div>
                            </div>

                            <div className={styles.fraudStats}>
                                <div className={styles.fraudStat}>
                                    <p className={styles.label}>পাঠাও</p>
                                    <p className={styles.value}>১২ / ৩২</p>
                                </div>
                                <div className={styles.fraudStat}>
                                    <p className={styles.label}>স্টেডফাস্ট</p>
                                    <p className={styles.value}>৮ / ২৪</p>
                                </div>
                                <div className={styles.fraudStat}>
                                    <p className={styles.label}>ক্যান্সেল রেট</p>
                                    <p className={`${styles.value} ${styles.danger}`}>৬২%</p>
                                </div>
                                <div className={styles.fraudStat}>
                                    <p className={styles.label}>ফ্রড</p>
                                    <p className={`${styles.value} ${styles.danger}`}>৩</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ─── TESTIMONIALS ────────────────────────────────── */}
            <section className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>উদ্যোক্তাদের কথা</p>
                        <h2 className={styles.sectionTitle}>
                            হাজারো উদ্যোক্তার <span className={styles.grad}>সফলতার গল্প</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            যারা আগে ইকমটেক বেছে নিয়েছেন — তাদের অভিজ্ঞতা শুনুন।
                        </p>
                    </div>

                    <div className={styles.testimonialGrid}>
                        {testimonials.map(t => (
                            <div key={t.name} className={styles.testimonialCard}>
                                <div className={styles.testimonialQuoteIcon}><Icon.Quote /></div>
                                <p className={styles.testimonialText}>“{t.text}”</p>
                                <div className={styles.testimonialFooter}>
                                    <div className={styles.testimonialAvatar} style={{ background: t.bg }}>{t.avatar}</div>
                                    <div className={styles.testimonialMeta}>
                                        <p className={styles.testimonialName}>{t.name}</p>
                                        <p className={styles.testimonialRole}>{t.role}</p>
                                    </div>
                                    <div className={styles.testimonialStars} style={{ marginLeft: 'auto' }}>
                                        <Icon.Star /><Icon.Star /><Icon.Star /><Icon.Star /><Icon.Star />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── FAQ ─────────────────────────────────────────── */}
            <section id="faq" className={styles.section} style={{ paddingTop: 0 }}>
                <div className={styles.container}>
                    <div className={styles.sectionHead}>
                        <p className={styles.sectionEyebrow}>প্রশ্ন ও উত্তর</p>
                        <h2 className={styles.sectionTitle}>
                            আপনার যা <span className={styles.grad}>জানার আছে</span>
                        </h2>
                        <p className={styles.sectionSub}>
                            ইকমটেক সম্পর্কে সাধারণ প্রশ্নগুলোর উত্তর এক জায়গায়।
                        </p>
                    </div>

                    <div className={styles.faqList}>
                        {faqs.map((f, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div
                                    key={i}
                                    className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ''}`}
                                >
                                    <button
                                        type="button"
                                        className={styles.faqQuestion}
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        aria-expanded={isOpen}
                                    >
                                        <span>{f.q}</span>
                                        <span className={styles.faqIcon}><Icon.Plus /></span>
                                    </button>
                                    <div className={styles.faqAnswerWrap}>
                                        <p className={styles.faqAnswer}>{f.a}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <p className={styles.faqContact}>
                        আরও প্রশ্ন আছে?{' '}
                        <a
                            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            WhatsApp-এ যোগাযোগ করুন <FiArrowRight style={{verticalAlign:'middle'}} />
                        </a>
                    </p>
                </div>
            </section>

            {/* ─── FINAL CTA ───────────────────────────────────── */}
            <section id="cta" className={styles.section}>
                <div className={styles.container}>
                    <div className={styles.ctaWrap}>
                        <h2 className={styles.ctaTitle}>
                            আপনার স্টোর। আপনার কাস্টমার।
                            <br />চলবে <span className={styles.grad}>ইকমটেকের</span> সাথে।
                        </h2>
                        <p className={styles.ctaSub}>
                            এখনই শুরু করুন — ডোমেইন কানেক্ট করুন, প্রোডাক্ট অ্যাড করুন, আজ থেকেই অর্ডার নিন।
                        </p>
                        <div className={styles.ctaButtons}>
                            <Link to="/?tab=signup" className={`${styles.btnPrimary} ${styles.btnLarge}`}>
                                স্টোর তৈরি করুন <Icon.ArrowRight />
                            </Link>
                            <Link to="/?tab=login" className={`${styles.btnGhost} ${styles.btnLarge}`}>
                                লগইন
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── FOOTER ──────────────────────────────────────── */}
            <footer className={styles.footer}>
                <div className={styles.container}>
                    <div className={styles.footerGrid}>

                        <div className={styles.footerBrand}>
                            <Link to="/" className={styles.brand} onClick={scrollToTop}>
                                <span className={styles.brandLogo}>
                                    <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="EcomTech" />
                                </span>
                                ইকমটেক
                            </Link>
                            <p>
                                বাংলাদেশী সেলারদের জন্য তৈরি কমপ্লিট কমার্স প্ল্যাটফর্ম।
                                নিজের ডোমেইনে, নিজের মতো করে বিক্রি করুন।
                            </p>
                        </div>

                        <div className={styles.footerCol}>
                            <h4>প্রোডাক্ট</h4>
                            <ul>
                                <li><a href="#features">ফিচার</a></li>
                                <li><a href="#showcase">ড্যাশবোর্ড</a></li>
                                <li><a href="#fraud">ফ্রড চেক</a></li>
                                <li><a href="#pricing">প্রাইসিং</a></li>
                            </ul>
                        </div>

                        <div className={styles.footerCol}>
                            <h4>অ্যাকাউন্ট</h4>
                            <ul>
                                <li><Link to="/?tab=signup">নতুন অ্যাকাউন্ট</Link></li>
                                <li><Link to="/?tab=login">লগইন</Link></li>
                                <li><Link to="/forgot-password">পাসওয়ার্ড ভুলে গেছেন?</Link></li>
                                <li><Link to="/teamlogin">টিম লগইন</Link></li>
                            </ul>
                        </div>

                        <div className={styles.footerCol}>
                            <h4>কোম্পানি</h4>
                            <ul>
                                <li><a href="https://ecomtechbd.com/about">আমাদের সম্পর্কে</a></li>
                                <li><a href="https://ecomtechbd.com/contact">যোগাযোগ</a></li>
                                <li><a href="https://ecomtechbd.com/privacy">প্রাইভেসি পলিসি</a></li>
                                <li><a href="https://ecomtechbd.com/terms">টার্মস</a></li>
                            </ul>
                        </div>

                    </div>

                    <div className={styles.footerBottom}>
                        <span>© {new Date().getFullYear()} ইকমটেক। সর্বস্বত্ব সংরক্ষিত।</span>
                        <span>মেইড ইন বাংলাদেশ 🇧🇩</span>
                    </div>
                </div>
            </footer>

            {/* ─── Floating WhatsApp button ────────────────────── */}
            <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.whatsappFab}
                aria-label="WhatsApp-এ যোগাযোগ করুন"
            >
                <span className={styles.whatsappPulse}></span>
                <Icon.Whatsapp />
                <span className={styles.whatsappLabel}>WhatsApp</span>
            </a>

        </div>
    );
}

export default LandingHome;
